import { FC, useState, useContext, useMemo } from "react";
import { useChartSeriesToggle } from "src/hooks/useChartSeriesToggle";
import styles from "./StockHolders.module.scss";
import { classNames } from "@/lib/utils";
import Module from "@/components/module/Module";
import { getStockHolder, getStockHoldersList, getStockHoldersRegisteredUnder } from "@/controllers/stock-holders";
import { getStockInstitutionalHolders } from "@/controllers/institutional";
import StockBuybacks, { useStockBuybacks } from "@/components/buybacks/StockBuybacks";
import { Stock } from "@/models/stock";
import { useAtomValue } from "jotai";
import { backgroundColorState, greyDarkerState, greyState, lighterBackgroundColorState, ortexColorState, textColorState, themeState, userState } from "@/lib/store";
import StockHoldersOverTimeChart from "../stock-holders-over-time/StockHoldersOverTimeChart";
import type { TimeSeries } from "@/components/ortex-charts/TimeSeriesChart";
import { DataGrid, MemoizedDataGrid } from "../data-grid/DataGrid";
import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Dialog, DialogActions, DialogContent, useMediaQuery } from "@mui/material";
import { ModuleContext } from "@/lib/Context";
interface PropsTypes {
    stock: Stock;
}

interface row {
    id: number;
    "beneficial owner": string;
    "shares held": number;
    "% of shares": number;
    "last reported": number;
    "last change": number;
    company_id: number;
    company_type_id: number;
}

const rowKey = "beneficial owner";

const StockHolders: FC<PropsTypes> = ({ stock }) => {
    const { obfuscated } = useContext(ModuleContext);

    const theme = useAtomValue(themeState);
    const grey = useAtomValue(greyState);
    const greyDarker = useAtomValue(greyDarkerState);
    const textColor = useAtomValue(textColorState);
    const user = useAtomValue(userState);
    const [highContrast, setHighContrast] = useState<boolean>(user.settings.module_settings["HighContrastDisplay->/settings"]?.value);
    const backgroundColor = useAtomValue(backgroundColorState);

    const lighterBackgroundColor = useAtomValue(lighterBackgroundColorState);
    const ortexColor = useAtomValue(ortexColorState);

    const [open, setOpen] = useState<boolean>(false);
    const [id, setId] = useState<number>();

    const isMobile = useMediaQuery("(max-width: 768px)");

    // The company itself is often the largest single buyer of its own stock, so filed
    // buybacks belong beside the institutional holders. Gated on `hasData` because most
    // companies have never repurchased, and an empty module on every one of them is worse
    // than no module.
    const { hasData: hasBuybacks } = useStockBuybacks(stock.tradingitem_id);

    // The grid keeps the first render's copy of this button; see useChartSeriesToggle.
    const { series, toggle, renderIcon } = useChartSeriesToggle(stock.tradingitem_id);
    const chartButton = useMemo(
        () => ({
            onClick: (_value: number | string, row: row) =>
                toggle(String(row[rowKey]), async () => {
                    const data = await getStockHolder(stock.stock_id, row.company_id);
                    return Object.entries(data ?? {}).map(([name, points]): TimeSeries => ({ id: name, title: name, type: "line", data: points as [number, number][], axis: "left", format: "compact", precision: 2 }));
                }),
            renderIcon: (_value: number | string, row: row) => renderIcon(String(row[rowKey])),
        }),
        [toggle, renderIcon, stock.stock_id]
    );

    const showRegisteredHolders = async (id: number, row: row) => {
        setOpen(true);
        setId(row.company_id);
    };

    return (
        <div className={classNames(styles[theme], styles.StockHolders)}>
            <div className={classNames(styles.table, hasBuybacks && styles.withBuybacks)}>
                <div className={styles.holdersGrid}>
                    <Module obfuscated={obfuscated} title="Holders" help={{ moduleId: "holders", modulePage: "V2_stock_holders" }} allowFullscreen>
                        <DataGrid
                            id={stock.stock_id}
                            fetcher={getStockHoldersList}
                            button={chartButton}
                            expand={{
                                fetcher: getStockHoldersRegisteredUnder,
                                text: "Registered Holders",
                                field: "company_id",
                            }}
                            extraButton={[{ onClick: showRegisteredHolders, icon: <AddIcon />, label: "Show registered holders" }]}
                        />
                    </Module>
                </div>
                <div className={styles.holdersGrid}>
                    <Module obfuscated={obfuscated} title="13F Filings (SEC)" help={{ moduleId: "holders_13f", modulePage: "V2_stock_holders" }} allowFullscreen>
                        <MemoizedDataGrid fetcher={getStockInstitutionalHolders} data={{ tradingitem_id: stock.tradingitem_id }} tableId="stock-holders-13f" />
                    </Module>
                </div>
                {hasBuybacks && (
                    <div className={styles.holdersGrid}>
                        <Module obfuscated={obfuscated} title="Buybacks (company repurchases)" help={{ moduleId: "holders_buybacks", modulePage: "V2_stock_holders" }} allowFullscreen>
                            <StockBuybacks tradingitemId={stock.tradingitem_id} />
                        </Module>
                    </div>
                )}
            </div>
            <div className={styles.chart}>
                <Module
                    title={`Holders over time in ${stock.exchangesymbol}:${stock.ticker}${isMobile ? "" : ` - ${stock.name}`}`}
                    help={{ moduleId: "holders_chart", modulePage: "V2_stock_holders" }}
                    allowFullscreen
                    noPadding={{ source: "body", type: "mobile" }}
                    feedback
                >
                    <StockHoldersOverTimeChart stock={stock} extraSeries={series} />
                </Module>
            </div>
            <Dialog open={open} onClose={() => setOpen(false)} fullScreen={isMobile} fullWidth={!isMobile} maxWidth={"lg"}>
                <DialogContent style={{ height: "650px", minWidth: isMobile ? "100%" : "600px" }}>
                    <Box sx={{ width: "100%", height: "100%" }}>{typeof id === "number" && <MemoizedDataGrid id={stock.stock_id} fetcher={getStockHoldersRegisteredUnder} data={{ companyId: id }} />}</Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default StockHolders;
